import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { BitcoinExtension } from '@magic-ext/bitcoin';
import { CUSTOM_RPC_BY_CHAIN } from '@package/smart-wallet/constants';
import * as bitcoin from 'bitcoinjs-lib';
import { Magic } from 'magic-sdk';

import { MagicContext } from './MagicContext';

export const MagicProvider = ({ children }: { children: ReactNode }) => {
  const [magic, setMagic] = useState<Magic<[BitcoinExtension]> | null>(null);
  const [eoaBtcWallet, setEoaBtcWallet] = useState<string | null>(null);

  /* Effects */
  useEffect(() => {
    if (import.meta.env.VITE_MAGIC_API_KEY) {
      const magicInstance = new Magic(import.meta.env.VITE_MAGIC_API_KEY, {
        locale: 'es',
        extensions: [
          new BitcoinExtension({
            rpcUrl: CUSTOM_RPC_BY_CHAIN[0],
            network:
              import.meta.env.VITE_ENV === 'production' ? 'mainnet' : 'testnet',
          }),
        ],
      });
      setMagic(magicInstance);
      (async () => {
        setEoaBtcWallet(await magicInstance.bitcoin.getPublicAddress());
      })();
    }
  }, []);

  /* Bitcoin Functions */
  const sendBitcoin = useCallback(
    async (params: {
      recipientAddress: string;
      amount: number; // in satoshis
    }): Promise<string> => {
      if (!magic) throw new Error('Magic not initialized');
      if (!eoaBtcWallet) throw new Error('Bitcoin wallet not initialized');

      try {
        const { recipientAddress, amount } = params;

        // Determine network based on environment
        const network =
          import.meta.env.VITE_ENV === 'production'
            ? bitcoin.networks.bitcoin
            : bitcoin.networks.testnet;

        // Determine Mempool API base URL based on environment
        const mempoolBaseUrl =
          import.meta.env.VITE_ENV === 'production'
            ? 'https://mempool.space/api'
            : 'https://mempool.space/testnet/api';

        // 1. Fetch UTXOs from Mempool.space
        const utxoResponse = await fetch(
          `${mempoolBaseUrl}/address/${eoaBtcWallet}/utxo`,
        );

        if (!utxoResponse.ok) {
          throw new Error('Failed to fetch UTXOs');
        }

        const utxos = await utxoResponse.json();

        // Filter only confirmed UTXOs
        const confirmedUtxos = utxos.filter(
          (utxo: any) => utxo.status?.confirmed === true,
        );

        if (confirmedUtxos.length === 0) {
          throw new Error('No confirmed UTXOs available for this address');
        }

        // 2. Get recommended fees from Mempool.space
        const feesResponse = await fetch(
          `${mempoolBaseUrl}/v1/fees/recommended`,
        );
        let feeRate = 1; // Default fallback: 1 sat/vB

        if (feesResponse.ok) {
          const fees = await feesResponse.json();
          // Use hourFee for reasonable confirmation time
          feeRate = fees.hourFee || 1;
        }

        // Estimate transaction size: 1 input + 2 outputs = ~250 vBytes
        const estimatedSize = 250;
        const estimatedFee = Math.ceil(feeRate * estimatedSize);

        // Deduct fee from the user's amount
        const recipientAmount = amount - estimatedFee;

        if (recipientAmount <= 0) {
          throw new Error(
            `Amount too small. Fee (${estimatedFee} sats) exceeds or equals amount (${amount} sats)`,
          );
        }

        // 3. Select smallest UTXO
        const suitableUtxos = confirmedUtxos
          .filter((utxo: any) => utxo.value >= amount)
          .sort((a: any, b: any) => a.value - b.value);

        if (suitableUtxos.length === 0) {
          const availableBalance = confirmedUtxos.reduce(
            (sum: number, utxo: any) => sum + utxo.value,
            0,
          );
          throw new Error(
            `Insufficient balance. Need ${amount} satoshis, but only have ${availableBalance} satoshis available`,
          );
        }

        const selectedUtxo = suitableUtxos[0];

        // 4. Create transaction
        const tx = new bitcoin.Transaction();

        // Convert txId hex string to Uint8Array (reversed for Bitcoin)
        // bitcoinjs-lib v7 expects Uint8Array, and we need to reverse it
        const txHashHex = selectedUtxo.txid;
        const txHash = Uint8Array.from(
          txHashHex.match(/.{1,2}/g)!.reverse(),
          (byte: string) => parseInt(byte, 16),
        );

        // Add input (UTXO)
        tx.addInput(txHash, selectedUtxo.vout);

        // Create output script for recipient
        const outputScript = bitcoin.address.toOutputScript(
          recipientAddress,
          network,
        );

        // Add output (recipient) - sending amount minus fee
        tx.addOutput(outputScript, BigInt(recipientAmount));

        // Calculate change and add change output if needed
        const change = selectedUtxo.value - amount;
        const dustLimit = 546; // Bitcoin dust limit in satoshis

        if (change > dustLimit) {
          const changeScript = bitcoin.address.toOutputScript(
            eoaBtcWallet,
            network,
          );
          tx.addOutput(changeScript, BigInt(change));
        } else if (change > 0) {
          // If change exists but is below dust limit, it becomes part of the fee
          console.log(
            `Change (${change} sats) below dust limit. Adding to fee.`,
          );
        }

        // 5. Get transaction hex
        const txHex = tx.toHex();

        // 6. Sign transaction with Magic
        const signedTransactionHex = await magic.bitcoin.signTransaction(
          txHex,
          0, // Input index to sign
        );

        // 7. Broadcast transaction to network using Mempool.space
        const broadcastResponse = await fetch(`${mempoolBaseUrl}/tx`, {
          method: 'POST',
          headers: {
            'Content-Type': 'text/plain',
          },
          body: signedTransactionHex,
        });

        if (!broadcastResponse.ok) {
          const errorText = await broadcastResponse.text();
          throw new Error(
            `Failed to broadcast transaction: ${errorText || 'Unknown error'}`,
          );
        }

        // Mempool returns the txid as plain text
        const txId = await broadcastResponse.text();

        return txId;
      } catch (error) {
        console.error('Error sending Bitcoin transaction:', error);
        throw error;
      }
    },
    [magic, eoaBtcWallet],
  );

  const getBitcoinAddress = useCallback(async (): Promise<string> => {
    if (!magic) throw new Error('Magic not initialized');

    try {
      // Get Bitcoin address from Magic
      const userMetadata = await magic.user.getInfo();
      const publicAddress = userMetadata.publicAddress;

      console.log('Bitcoin address:', publicAddress);
      return publicAddress || '';
    } catch (error) {
      console.error('Error getting Bitcoin address:', error);
      throw error;
    }
  }, [magic]);

  /* Memo */
  const value = useMemo(() => {
    return {
      magic,
      bitcoin: {
        address: eoaBtcWallet,
        send: sendBitcoin,
        getAddress: getBitcoinAddress,
      },
    };
  }, [magic, eoaBtcWallet, sendBitcoin, getBitcoinAddress]);

  /* Return */
  return (
    <MagicContext.Provider value={value}>{children}</MagicContext.Provider>
  );
};
